import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { vacationApi, userApi, calendarApi } from '../utils/api';import { motion, AnimatePresence } from 'framer-motion';
import {
  CalendarDays, AlertCircle, CheckCircle, ChevronRight,
  Palmtree, TrendingDown, Info, Loader, X, Star, Clock, Building2, History
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

function AnimatedCounter({ value, duration = 600 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (value === 0) { setDisplay(0); return; }
    let start = 0;
    const steps = 24;
    const increment = value / steps;
    const interval = duration / steps;
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) { setDisplay(value); clearInterval(timer); }
      else setDisplay(parseFloat(start.toFixed(1)));
    }, interval);
    return () => clearInterval(timer);
  }, [value, duration]);
  return <span>{display}</span>;
}

const TYPE_COLORS = {
  nacional: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', dot: '#ef4444' },
  autonomico: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', dot: '#f97316' },
  local: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', dot: '#f59e0b' },
  empresa: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', dot: '#a855f7' },
  weekend: { bg: 'bg-slate-100 dark:bg-slate-800/50', text: 'text-slate-500 dark:text-slate-400', dot: '#94a3b8' },
};

const typeLabels = {
  nacional: 'Nacional', autonomico: 'Autonómico', local: 'Local',
  empresa: 'Valoriza', weekend: 'Fin de semana',
};

export default function VacationRequestForm() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [history, setHistory] = useState([]);

  // Min date = tomorrow (vacations must be requested at least 1 day in advance)
  const tomorrow = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  })();

  useEffect(() => {
    userApi.getBalance(user.id).then(r => setBalance(r.data)).catch(console.error);
    vacationApi.list().then(r => setHistory(r.data || [])).catch(console.error);
    // Load holidays for current year and next
    const yr = new Date().getFullYear();
    Promise.all([calendarApi.holidays(yr), calendarApi.holidays(yr + 1)])
      .then(([r1, r2]) => setHolidays([...r1.data, ...r2.data]))
      .catch(console.error);
  }, [user.id]);

  const fetchBreakdown = useCallback(async (start, end) => {
    if (!start || !end || end < start) { setBreakdown(null); return; }
    setLoadingBreakdown(true);
    try {
      const res = await calendarApi.breakdown(start, end);
      setBreakdown(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingBreakdown(false); }
  }, []);

  useEffect(() => {
    if (startDate && endDate && endDate >= startDate) fetchBreakdown(startDate, endDate);
    else setBreakdown(null);
  }, [startDate, endDate, fetchBreakdown]);

  const vacAvail = balance ? balance.vacation_total - balance.vacation_used - balance.vacation_pending : 24;
  const carryoverAvail = balance ? Math.max(0, balance.carryover_days - balance.carryover_used - balance.carryover_pending) : 0;
  const today = new Date();
  const carryoverCutoff = new Date(today.getFullYear(), 3, 30);
  const hasCarryover = carryoverAvail > 0 && today <= carryoverCutoff;
  const totalAvail = vacAvail + (hasCarryover ? carryoverAvail : 0);
  const workingDays = breakdown?.working_days || 0;
  const isOver = workingDays > totalAvail;
  const isValid = startDate && endDate && endDate >= startDate && workingDays > 0 && !isOver;
  const pctAfter = balance ? Math.min(100, ((balance.vacation_used + workingDays) / balance.vacation_total) * 100) : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await vacationApi.create({ start_date: startDate, end_date: endDate, notes });
      setSuccess(true);
      setTimeout(() => navigate('/my-requests'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear la solicitud');
    } finally {
      setLoading(false);
    }
  };

  if (success) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-card border border-border rounded-2xl p-10 text-center shadow-xl max-w-sm"
      >
        <motion.div
          initial={{ scale: 0 }} animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-brand mx-auto mb-5 flex items-center justify-center shadow-lg"
        >
          <CheckCircle className="h-10 w-10 text-white" />
        </motion.div>
        <h2 className="text-xl font-bold font-heading mb-2">¡Solicitud enviada!</h2>
        <p className="text-sm text-muted-foreground">Tu solicitud está pendiente de aprobación por tu manager.</p>
        <div className="mt-4 text-sm font-semibold text-brand">
          {workingDays} días laborables · {startDate} → {endDate}
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-green-400 to-brand shadow-md">
            <Palmtree className="h-5 w-5 text-white" />
          </div>
          <h1 className="text-2xl font-bold font-heading">{t('newVacation')}</h1>
        </div>
        <p className="text-sm text-muted-foreground ml-12">Selecciona el período y revisa el desglose de días en tiempo real</p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* LEFT: Form */}
        <motion.div
          initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-3"
        >
          <form onSubmit={handleSubmit} className="bg-card border border-border rounded-2xl p-6 shadow-sm space-y-5">
            {/* Date range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="start-date" className="text-sm font-medium flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5 text-brand" />{t('startDate')} *
                </Label>
                <Input
                  id="start-date" type="date" value={startDate}
                  min={tomorrow}
                  onChange={e => { setStartDate(e.target.value); if (endDate < e.target.value) setEndDate(''); }}
                  required data-testid="vacation-start-date"
                  className="focus-visible:ring-brand h-10"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="end-date" className="text-sm font-medium flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5 text-brand" />{t('endDate')} *
                </Label>
                <Input
                  id="end-date" type="date" value={endDate}
                  min={startDate || tomorrow}
                  onChange={e => setEndDate(e.target.value)}
                  required data-testid="vacation-end-date"
                  className="focus-visible:ring-brand h-10"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <Label htmlFor="notes" className="text-sm font-medium">{t('notes')} (opcional)</Label>
              <Textarea
                id="notes" value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Motivo o comentario adicional..." rows={3}
                data-testid="vacation-notes" className="resize-none focus-visible:ring-brand"
              />
            </div>

            {/* Carryover notice */}
            {hasCarryover && (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="flex items-start gap-2 p-3 rounded-xl bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 text-sm"
              >
                <Clock className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-orange-700 dark:text-orange-400">
                    Tienes {carryoverAvail} días arrastrados disponibles
                  </span>
                  <span className="text-orange-600/70 dark:text-orange-400/70 ml-1 text-xs">
                    (válidos hasta el 30 de abril)
                  </span>
                </div>
              </motion.div>
            )}

            {/* Balance info */}
            <div className="grid grid-cols-3 gap-2 text-center">
              {[
                { label: 'Disponibles', value: vacAvail.toFixed(1), color: 'text-brand', bg: 'bg-green-50 dark:bg-green-900/20' },
                { label: 'Solicitados', value: workingDays > 0 ? workingDays.toFixed(1) : '—', color: workingDays > vacAvail ? 'text-destructive' : 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20' },
                { label: 'Quedarán', value: workingDays > 0 ? Math.max(0, vacAvail - workingDays).toFixed(1) : vacAvail.toFixed(1), color: (vacAvail - workingDays) < 3 ? 'text-red-600' : 'text-muted-foreground', bg: 'bg-muted/40' },
              ].map(item => (
                <div key={item.label} className={`${item.bg} rounded-xl p-3 border border-border/50`}>
                  <div className={`text-xl font-bold font-heading ${item.color}`}>{item.value}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>

            {/* Balance bar */}
            {balance && (
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Uso de vacaciones {new Date().getFullYear()}</span>
                  <span>{balance.vacation_used + workingDays} / {balance.vacation_total} días</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full flex">
                    <div className="bg-brand transition-all duration-500" style={{ width: `${(balance.vacation_used / balance.vacation_total) * 100}%` }} />
                    <motion.div
                      className="bg-blue-400"
                      animate={{ width: `${workingDays > 0 ? (workingDays / balance.vacation_total) * 100 : 0}%` }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                </div>
                <div className="flex gap-3 text-xs">
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-brand" />Usados</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-blue-400" />Esta solicitud</span>
                </div>
              </div>
            )}

            {isOver && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="flex items-start gap-2 p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-sm text-destructive"
                data-testid="vacation-balance-error"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Saldo insuficiente. Disponibles: {totalAvail.toFixed(1)} días</span>
              </motion.div>
            )}

            {error && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="flex items-start gap-2 p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-sm text-destructive"
                data-testid="vacation-error"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}

            <div className="flex gap-3 pt-1">
              <Button type="button" variant="outline" onClick={() => navigate(-1)} className="flex-none">Cancelar</Button>
              <motion.div className="flex-1" whileHover={{ scale: isValid ? 1.01 : 1 }} whileTap={{ scale: isValid ? 0.99 : 1 }}>
                <Button
                  type="submit" disabled={loading || !isValid}
                  data-testid="vacation-submit"
                  className="w-full h-11 bg-gradient-to-r from-brand to-green-500 hover:from-brand-hover hover:to-green-600 text-white font-semibold shadow-md transition-all"
                >
                  {loading
                    ? <><Loader className="h-4 w-4 animate-spin mr-2" />Enviando...</>
                    : <><CalendarDays className="h-4 w-4 mr-2" />Solicitar {workingDays > 0 ? `${workingDays} días` : 'vacaciones'}</>
                  }
                </Button>
              </motion.div>
            </div>
          </form>
        </motion.div>

        {/* RIGHT: Live summary panel */}
        <motion.div
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2 space-y-4"
        >
          {/* Counter */}
          <div className={`rounded-2xl p-6 text-center border shadow-sm transition-all duration-300 ${
            workingDays > 0
              ? isOver
                ? 'bg-destructive/10 border-destructive/30'
                : 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800'
              : 'bg-muted/30 border-border'
          }`}>
            <motion.div
              key={workingDays}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative"
            >
              <div className={`text-6xl font-bold font-heading ${workingDays > 0 ? isOver ? 'text-destructive' : 'text-brand' : 'text-muted-foreground/30'}`}>
                {workingDays > 0 ? <AnimatedCounter value={workingDays} /> : '—'}
              </div>
              {loadingBreakdown && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader className="h-5 w-5 animate-spin text-brand" />
                </div>
              )}
            </motion.div>
            <div className="text-sm font-medium mt-1 text-muted-foreground">
              {workingDays > 0 ? 'días laborables' : 'Selecciona un período'}
            </div>
            {breakdown && breakdown.total_days > 0 && (
              <div className="mt-3 text-xs text-muted-foreground">
                {breakdown.total_days} días totales
                {breakdown.weekends > 0 && ` · ${breakdown.weekends} fines de semana`}
                {breakdown.holidays > 0 && ` · ${breakdown.holidays} festivos`}
              </div>
            )}
          </div>

          {/* Breakdown detail */}
          <AnimatePresence>
            {breakdown && startDate && endDate && (
              <motion.div
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                className="bg-card border border-border rounded-2xl p-4 shadow-sm"
              >
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Desglose del período</h3>

                {/* Summary rows */}
                <div className="space-y-2 mb-3">
                  {[
                    { label: 'Días totales', value: breakdown.total_days, color: 'text-foreground', icon: '📅' },
                    { label: 'Fines de semana', value: `-${breakdown.weekends}`, color: 'text-slate-400', icon: '🏖️' },
                    { label: 'Festivos', value: `-${breakdown.holidays}`, color: 'text-orange-500', icon: '🎉' },
                    { label: 'Días laborables', value: breakdown.working_days, color: 'text-brand font-bold', icon: '✅', divider: true },
                  ].map((row) => (
                    <div key={row.label}>
                      {row.divider && <div className="border-t border-border my-2" />}
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                          <span>{row.icon}</span>{row.label}
                        </span>
                        <span className={`font-semibold ${row.color}`}>{row.value}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Holiday list */}
                {breakdown.holiday_list?.length > 0 && (
                  <div className="space-y-1.5 mt-3 border-t border-border pt-3">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Festivos en el período:</p>
                    {breakdown.holiday_list.map((h, i) => {
                      const typeStyle = TYPE_COLORS[h.type] || TYPE_COLORS.nacional;
                      return (
                        <motion.div
                          key={h.date}
                          initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.05 }}
                          className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg ${typeStyle.bg}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: typeStyle.dot }} />
                            <span className={`text-xs font-medium ${typeStyle.text}`}>{h.name}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-muted-foreground">
                              {new Date(h.date + 'T12:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                            </span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${typeStyle.bg} ${typeStyle.text} font-medium`}>
                              {typeLabels[h.type] || h.type}
                            </span>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Policy reminder */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
            className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-3"
          >
            <div className="flex items-start gap-2">
              <Info className="h-3.5 w-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                <p className="font-medium">Política de vacaciones 2026</p>
                <p>≥50% entre junio y septiembre</p>
                <p>Máximo 4 períodos al año</p>
                <p>Fecha límite: 31 de marzo 2026</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Vacation History */}
      {history.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8"
        >
          <div className="flex items-center gap-2 mb-3">
            <History className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Historial de solicitudes</h2>
          </div>
          <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Período</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Días</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Estado</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Solicitado</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Notas</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((vac) => {
                    const statusMap = {
                      pending: { label: 'Pendiente', cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
                      approved: { label: 'Aprobado', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
                      rejected: { label: 'Rechazado', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
                      cancelled: { label: 'Cancelado', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400' },
                      cancellation_requested: { label: 'Cancelación pend.', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
                    };
                    const st = statusMap[vac.status] || { label: vac.status, cls: 'bg-muted text-muted-foreground' };
                    return (
                      <tr key={vac.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3 font-medium">
                          {new Date(vac.start_date + 'T12:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {' → '}
                          {new Date(vac.end_date + 'T12:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-4 py-3">{vac.working_days}d</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {new Date(vac.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs max-w-[200px] truncate">{vac.notes || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
